import './VerifyEmailView.css'

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { session } from '../store'

export function VerifyEmailView() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [hasAttempted, setHasAttempted] = useState(false)

  const isLoading = useAtomValue(session.verifyEmailIsLoadingAtom)
  const issues = useAtomValue(session.verifyEmailIssuesAtom)
  const authStatus = useAtomValue(session.sessionStatusAtom)

  const verifyEmail = useSetAtom(session.verifyEmailAtom)
  const navigate = useNavigate()

  useEffect(() => {
    if (authStatus === 'authenticated') {
      navigate('/profile', { replace: true })
    }
  }, [authStatus, navigate])

  useEffect(() => {
    if (!token || hasAttempted) return

    setHasAttempted(true)
    verifyEmail(token)
  }, [token, hasAttempted, verifyEmail])

  const tokenError = issues.find((i) => 'token' in i)

  if (!token) {
    return (
      <div className="verify-email-view verify-email-view--error">
        <div className="verify-email-view__error-icon">!</div>
        <h2 className="verify-email-view__title">Invalid Link</h2>
        <p className="verify-email-view__message">
          This verification link is invalid. Please check your email for the correct link.
        </p>
        <Link to="/register" className="verify-email-view__link">
          Back to registration
        </Link>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="verify-email-view verify-email-view--loading">
        <div className="verify-email-view__spinner" />
        <h2 className="verify-email-view__title">Verifying your email...</h2>
      </div>
    )
  }

  if (tokenError) {
    const errorMessage = getErrorMessage(tokenError['token'] as string)
    return (
      <div className="verify-email-view verify-email-view--error">
        <div className="verify-email-view__error-icon">!</div>
        <h2 className="verify-email-view__title">Verification Failed</h2>
        <p className="verify-email-view__message">{errorMessage}</p>
        <Link to="/register" className="verify-email-view__link">
          Back to registration
        </Link>
      </div>
    )
  }

  // Success state is handled by redirect to home
  return null
}

function getErrorMessage(code: string): string {
  switch (code) {
    case 'invalidOrExpired':
      return 'This verification link is invalid or has expired. Please register again to receive a new link.'
    case 'alreadyVerified':
      return 'This email has already been verified. You can sign in to your account.'
    case 'networkError':
      return 'Unable to verify your email. Please check your connection and try again.'
    default:
      return 'Unable to verify your email. Please try again or register for a new account.'
  }
}
